export type ThemeId = 'midnight' | 'slate' | 'forest' | 'ocean' | 'dusk';

export const THEME_IDS: ThemeId[] = ['midnight', 'slate', 'forest', 'ocean', 'dusk'];

export const DEFAULT_THEME: ThemeId = 'midnight';

export interface ThemeDefinition {
  id: ThemeId;
  name: string;
  description: string;
  preview: [string, string, string];
  vars: Record<string, string>;
}

function buildThemeVars(
  base: Record<string, string>,
  accentPrimary: string,
  accentPrimaryDark: string,
  accentSecondary: string
): Record<string, string> {
  return {
    ...base,
    '--accent-primary': accentPrimary,
    '--accent-primary-dark': accentPrimaryDark,
    '--accent-secondary': accentSecondary,
    '--accent-green': accentPrimary,
    '--accent-cyan': accentSecondary,
    '--accent-primary-soft': `${accentPrimary}1a`,
    '--accent-primary-border': `${accentPrimary}4d`,
    '--accent-primary-glow': `${accentPrimary}59`,
    '--gradient-primary': `linear-gradient(135deg, ${accentPrimary}, ${accentPrimaryDark})`,
    '--gradient-logo': `linear-gradient(135deg, ${accentPrimary}, ${accentSecondary})`,
    '--gradient-scrollbar': `linear-gradient(180deg, ${accentPrimary}, ${accentSecondary})`,
    '--shadow-glow': `0 4px 20px ${accentPrimary}40`,
    '--shadow-glow-lg': `0 8px 32px ${accentPrimary}35`,
  };
}

export const themes: Record<ThemeId, ThemeDefinition> = {
  midnight: {
    id: 'midnight',
    name: 'Gece Cam',
    description: 'Varsayılan premium görünüm',
    preview: ['#060918', '#22c55e', '#06b6d4'],
    vars: buildThemeVars(
      {
        '--text-primary': '#ffffff',
        '--text-secondary': '#e2e8f0',
        '--text-muted': '#94a3b8',
        '--text-dim': '#64748b',
        '--text-on-accent': '#ffffff',
        '--bg-deep': '#060918',
        '--bg-surface': 'rgba(10, 15, 30, 0.95)',
        '--bg-card': 'rgba(15, 23, 42, 0.45)',
        '--bg-header': 'rgba(6, 9, 24, 0.92)',
        '--surface-muted': 'rgba(30, 41, 59, 0.45)',
        '--surface-elevated': 'rgba(15, 23, 42, 0.72)',
        '--footer-bg': 'rgba(10, 15, 30, 0.9)',
        '--modal-bg': 'rgba(10, 15, 30, 0.98)',
        '--input-bg': 'rgba(15, 23, 42, 0.55)',
        '--accent-purple': '#8b5cf6',
        '--accent-danger': '#f87171',
        '--accent-warning': '#fbbf24',
        '--glass-border': 'rgba(51, 65, 85, 0.35)',
        '--border-subtle': 'rgba(51, 65, 85, 0.25)',
        '--border-strong': 'rgba(71, 85, 105, 0.45)',
        '--mesh-color-1': 'rgba(34, 197, 94, 0.3)',
        '--mesh-color-2': 'rgba(6, 182, 212, 0.2)',
        '--mesh-color-3': 'rgba(139, 92, 246, 0.15)',
        '--scrollbar-track': '#0a0f1e',
      },
      '#22c55e',
      '#16a34a',
      '#06b6d4'
    ),
  },
  slate: {
    id: 'slate',
    name: 'Arduvaz',
    description: 'Soğuk steel-mavi, gümüş vurgular',
    preview: ['#0a101c', '#7cb8ff', '#a8b4c8'],
    vars: buildThemeVars(
      {
        '--text-primary': '#f1f5f9',
        '--text-secondary': '#dbeafe',
        '--text-muted': '#94a3b8',
        '--text-dim': '#64748b',
        '--text-on-accent': '#0f172a',
        '--bg-deep': '#0a101c',
        '--bg-surface': 'rgba(12, 18, 32, 0.97)',
        '--bg-card': 'rgba(30, 41, 59, 0.52)',
        '--bg-header': 'rgba(10, 16, 28, 0.9)',
        '--surface-muted': 'rgba(51, 65, 85, 0.4)',
        '--surface-elevated': 'rgba(30, 41, 59, 0.78)',
        '--footer-bg': 'rgba(10, 14, 26, 0.94)',
        '--modal-bg': 'rgba(12, 18, 32, 0.98)',
        '--input-bg': 'rgba(15, 23, 42, 0.6)',
        '--accent-purple': '#818cf8',
        '--accent-danger': '#fca5a5',
        '--accent-warning': '#fcd34d',
        '--glass-border': 'rgba(100, 116, 139, 0.38)',
        '--border-subtle': 'rgba(71, 85, 105, 0.28)',
        '--border-strong': 'rgba(148, 163, 184, 0.35)',
        '--mesh-color-1': 'rgba(124, 184, 255, 0.16)',
        '--mesh-color-2': 'rgba(168, 180, 200, 0.1)',
        '--mesh-color-3': 'rgba(129, 140, 248, 0.1)',
        '--scrollbar-track': '#0f172a',
      },
      '#7cb8ff',
      '#5b9cf0',
      '#a8b4c8'
    ),
  },
  forest: {
    id: 'forest',
    name: 'Orman',
    description: 'Zengin moss-emerald, sıcak koyu zemin',
    preview: ['#061410', '#4ade80', '#86efac'],
    vars: buildThemeVars(
      {
        '--text-primary': '#ecfdf5',
        '--text-secondary': '#d1fae5',
        '--text-muted': '#86efac',
        '--text-dim': '#6ee7b7',
        '--text-on-accent': '#052e16',
        '--bg-deep': '#061410',
        '--bg-surface': 'rgba(8, 20, 16, 0.97)',
        '--bg-card': 'rgba(12, 32, 24, 0.52)',
        '--bg-header': 'rgba(6, 20, 14, 0.9)',
        '--surface-muted': 'rgba(20, 40, 32, 0.45)',
        '--surface-elevated': 'rgba(15, 35, 28, 0.78)',
        '--footer-bg': 'rgba(6, 16, 12, 0.94)',
        '--modal-bg': 'rgba(8, 20, 16, 0.98)',
        '--input-bg': 'rgba(10, 25, 20, 0.6)',
        '--accent-purple': '#059669',
        '--accent-danger': '#f87171',
        '--accent-warning': '#fbbf24',
        '--glass-border': 'rgba(74, 222, 128, 0.22)',
        '--border-subtle': 'rgba(52, 211, 153, 0.18)',
        '--border-strong': 'rgba(74, 222, 128, 0.32)',
        '--mesh-color-1': 'rgba(74, 222, 128, 0.14)',
        '--mesh-color-2': 'rgba(16, 185, 129, 0.1)',
        '--mesh-color-3': 'rgba(5, 150, 105, 0.1)',
        '--scrollbar-track': '#0a1512',
      },
      '#4ade80',
      '#10b981',
      '#86efac'
    ),
  },
  ocean: {
    id: 'ocean',
    name: 'Okyanus',
    description: 'Derin navy, aqua-cyan kontrast',
    preview: ['#040c14', '#2dd4bf', '#38bdf8'],
    vars: buildThemeVars(
      {
        '--text-primary': '#f0fdfa',
        '--text-secondary': '#ccfbf1',
        '--text-muted': '#94a3b8',
        '--text-dim': '#64748b',
        '--text-on-accent': '#042f2e',
        '--bg-deep': '#040c14',
        '--bg-surface': 'rgba(6, 16, 28, 0.97)',
        '--bg-card': 'rgba(10, 28, 44, 0.52)',
        '--bg-header': 'rgba(4, 12, 20, 0.9)',
        '--surface-muted': 'rgba(15, 35, 50, 0.45)',
        '--surface-elevated': 'rgba(12, 30, 48, 0.78)',
        '--footer-bg': 'rgba(4, 10, 18, 0.94)',
        '--modal-bg': 'rgba(6, 16, 28, 0.98)',
        '--input-bg': 'rgba(8, 22, 36, 0.6)',
        '--accent-purple': '#0ea5e9',
        '--accent-danger': '#f87171',
        '--accent-warning': '#fbbf24',
        '--glass-border': 'rgba(45, 212, 191, 0.24)',
        '--border-subtle': 'rgba(51, 65, 85, 0.28)',
        '--border-strong': 'rgba(45, 212, 191, 0.35)',
        '--mesh-color-1': 'rgba(45, 212, 191, 0.14)',
        '--mesh-color-2': 'rgba(56, 189, 248, 0.11)',
        '--mesh-color-3': 'rgba(14, 165, 233, 0.1)',
        '--scrollbar-track': '#081420',
      },
      '#2dd4bf',
      '#14b8a6',
      '#38bdf8'
    ),
  },
  dusk: {
    id: 'dusk',
    name: 'Alacakaranlık',
    description: 'Sıcak charcoal, soft violet-rose',
    preview: ['#0c0814', '#c4b5fd', '#f0abfc'],
    vars: buildThemeVars(
      {
        '--text-primary': '#faf5ff',
        '--text-secondary': '#ede9fe',
        '--text-muted': '#a8a29e',
        '--text-dim': '#78716c',
        '--text-on-accent': '#2e1065',
        '--bg-deep': '#0c0814',
        '--bg-surface': 'rgba(16, 10, 26, 0.97)',
        '--bg-card': 'rgba(32, 22, 48, 0.52)',
        '--bg-header': 'rgba(12, 8, 20, 0.9)',
        '--surface-muted': 'rgba(40, 30, 55, 0.45)',
        '--surface-elevated': 'rgba(28, 20, 42, 0.78)',
        '--footer-bg': 'rgba(10, 6, 18, 0.94)',
        '--modal-bg': 'rgba(14, 10, 24, 0.98)',
        '--input-bg': 'rgba(20, 14, 32, 0.6)',
        '--accent-purple': '#a78bfa',
        '--accent-danger': '#fb7185',
        '--accent-warning': '#fbbf24',
        '--glass-border': 'rgba(167, 139, 250, 0.24)',
        '--border-subtle': 'rgba(87, 83, 78, 0.28)',
        '--border-strong': 'rgba(167, 139, 250, 0.35)',
        '--mesh-color-1': 'rgba(196, 181, 253, 0.12)',
        '--mesh-color-2': 'rgba(240, 171, 252, 0.08)',
        '--mesh-color-3': 'rgba(124, 58, 237, 0.1)',
        '--scrollbar-track': '#120c1a',
      },
      '#c4b5fd',
      '#a78bfa',
      '#f0abfc'
    ),
  },
};

export function getTheme(id: string): ThemeDefinition {
  return themes[id as ThemeId] || themes[DEFAULT_THEME];
}

export function applyTheme(themeId: ThemeId): void {
  const theme = getTheme(themeId);
  const root = document.documentElement;
  Object.entries(theme.vars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
  root.setAttribute('data-theme', themeId);
  root.classList.add('theme-transitioning');
  window.setTimeout(() => root.classList.remove('theme-transitioning'), 300);
}

export function isValidThemeId(id: string): id is ThemeId {
  return THEME_IDS.includes(id as ThemeId);
}

/** Inline style / framer-motion için mevcut tema değişkenleri */
export function getThemeCssVars(themeId: ThemeId): Record<string, string> {
  return getTheme(themeId).vars;
}
